import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  CheckCircle2,
  Clock,
  Users,
  AlertTriangle,
  ThumbsUp,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import type { SummaryResponse } from "@/lib/types";
import { fmt, fmtMs, cn } from "@/lib/utils";

/** 최근 24시간 KPI. 30초마다 자동 갱신. */
export function KpiCards() {
  const { data, isLoading } = useQuery({
    queryKey: ["summary", "today"],
    queryFn: () => {
      const now = new Date();
      const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return api.get<SummaryResponse>("/stats/summary", {
        from: start.toISOString(),
        to: now.toISOString(),
      });
    },
    refetchInterval: 30_000,
  });

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }

  const ratePct = data.answer_rate !== null
    ? `${(data.answer_rate * 100).toFixed(1)}%`
    : "-";
  const fbTotal = data.pos_feedback + data.neg_feedback;
  const posRate = fbTotal > 0
    ? `${((data.pos_feedback / fbTotal) * 100).toFixed(0)}%`
    : "-";

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      <Kpi icon={Activity} label="총 질의 (24h)" value={fmt(data.total)} />
      <Kpi icon={CheckCircle2} label="답변율" value={ratePct} tone="success" />
      <Kpi icon={Clock} label="평균 응답" value={fmtMs(data.avg_response_ms)} />
      <Kpi icon={Users} label="활성 사용자" value={fmt(data.active_users)} />
      <Kpi
        icon={AlertTriangle}
        label="미답변"
        value={fmt(data.unanswered)}
        tone={data.unanswered > 0 ? "warn" : undefined}
      />
      <Kpi
        icon={ThumbsUp}
        label="👍 비율"
        value={posRate}
        sub={`${fmt(data.pos_feedback)} / ${fmt(data.neg_feedback)}`}
      />
    </div>
  );
}

function Kpi({
  icon: Icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: typeof Activity;
  label: string;
  value: string;
  sub?: string;
  tone?: "success" | "warn";
}) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <Icon
          className={cn(
            "h-5 w-5 shrink-0 mt-1",
            tone === "success" ? "text-green-600" :
            tone === "warn" ? "text-orange-600" : "text-muted-foreground",
          )}
        />
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="mt-0.5 text-xl font-semibold tabular-nums">{value}</div>
          {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}
